import csLocale from "date-fns/locale/cs";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import format from "date-fns/format";
import parse from "date-fns/parse";
import isValid from "date-fns/isValid";
import parseISO from "date-fns/parseISO";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import getDate from "date-fns/getDate";

export default class SafeDate {
  private readonly instance: Date | null = null;

  constructor(
    value?: Date | string | { year: number; month: number; day: number },
  ) {
    try {
      if (typeof value === "string") {
        this.instance = parseISO(value);
      } else if (value instanceof Date) {
        this.instance = value;
      } else if (
        typeof value === "object" &&
        (value.year || value.month || value.day)
      ) {
        const { year, month, day } = value;

        this.instance = parse(`${year}-${month}-${day}`, "y-M-d", new Date());
      } else {
        this.instance = new Date();
      }

      if (!isValid(this.instance)) {
        this.instance = null;
      }
    } catch (error) {
      // Sentry.captureException(error);

      this.instance = null;
    }
  }

  get year(): number {
    return this.instance && this.isValid ? getYear(this.instance) : 0;
  }

  get month(): number {
    return this.instance && this.isValid ? getMonth(this.instance) + 1 : 1;
  }

  get day(): number {
    return this.instance && this.isValid ? getDate(this.instance) : 1;
  }

  get isValid(): boolean {
    return Boolean(this.instance) && isValid(this.instance);
  }

  formatTimeAgo(): string {
    return this.instance && this.isValid
      ? formatDistanceToNow(this.instance, {
          locale: csLocale,
          addSuffix: true,
        })
      : "";
  }

  formatDateAsISO(): string {
    return this.instance && this.isValid
      ? format(this.instance, "yyyy-MM-dd")
      : "";
  }

  formatDateAsNumeric(): string {
    return this.instance && this.isValid
      ? format(this.instance, "d. M. yyyy")
      : "";
  }

  formatDateAsNumericWithLeadingZeros(): string {
    return this.instance && this.isValid
      ? format(this.instance, "dd.MM.yyyy")
      : "";
  }

  formatMonthAsWord(): string {
    return this.instance && this.isValid
      ? format(this.instance, "MMMM", { locale: csLocale })
      : "";
  }

  formatYearAsWord(): string {
    return this.instance && this.isValid ? format(this.instance, "yyyy") : "";
  }

  formatTime(): string {
    return this.instance && this.isValid ? format(this.instance, "HH:mm") : "";
  }

  getTimestampMs(): number {
    return this.instance && this.isValid ? this.instance.getTime() : 0;
  }

  getTimestampSec(): number {
    return this.getTimestampMs() / 1000;
  }
}
